'use client'

import { Button } from '@brew/ui/button'
import { Input } from '@brew/ui/input'
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react'
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  BrewAlertDialogContent,
  BrewDialogBody,
  BrewDialogFooter,
  BrewDialogHeader,
  BrewDialogMedal,
} from '@/components/ui/brew-dialog'
import {
  AlertTriangleIcon,
  CircleHelpIcon,
  type LucideIcon,
} from '@/components/ui/brew-dialog/icons'

export type ConfirmDialogRenderSlots = {
  title: ReactNode
  description: ReactNode | undefined
  /** Caller body followed by the type-to-confirm field when one is requested. */
  body: ReactNode | undefined
  actions: ReactNode
}

type ConfirmTypeToConfirm = {
  /** The exact text the user must type before the confirm button enables. */
  phrase: string
  /** Prompt above the field. Defaults to "Type <phrase> to confirm". */
  label?: ReactNode
}

export type ConfirmOptions = {
  /** Dialog heading. */
  title: ReactNode
  /** Optional supporting copy shown under the title. */
  description?: ReactNode
  /**
   * Optional structured content between the header and the buttons. Use this
   * for lists — `description` renders inside a paragraph, so block content
   * belongs here.
   */
  body?: ReactNode
  /** Confirm button label. Defaults to "Confirm". */
  confirmLabel?: string
  /** Cancel button label. Defaults to "Cancel". */
  cancelLabel?: string
  /** Render the confirm button as a destructive (red) action. */
  destructive?: boolean
  /**
   * The medal glyph above the title. Defaults to a warning triangle for
   * destructive confirms and a question mark otherwise; pass the object of
   * the action (trash, mail, key…) when it reads clearer.
   */
  icon?: LucideIcon
  /**
   * Type-to-confirm gate: the confirm button stays disabled until the user
   * types `phrase` exactly. Reserve it for irreversible, high-blast-radius
   * actions (deleting a whole automation) — a routine confirm carries none.
   */
  typeToConfirm?: ConfirmTypeToConfirm
  /**
   * Keep the pre-v2 confirm chrome instead of the dialog kit. The Send
   * dialog stays on its own design, confirms included.
   */
  classic?: boolean
  /** Optional domain presentation that preserves this root-mounted lifecycle. */
  contentRenderer?: (slots: ConfirmDialogRenderSlots) => ReactNode
  /**
   * Runs when the user confirms. While it is pending the dialog stays open
   * with both buttons disabled and Escape / overlay clicks ignored, so a slow
   * destructive action cannot be re-triggered and the user can see that it is
   * in flight. The `confirm()` promise resolves `true` once it settles. A
   * rejection still closes the dialog and is re-thrown on the provider's
   * chain (an unhandled rejection, exactly as a `void action()` would be), so
   * surface failures inside the action itself — the toast-bound lifecycle
   * actions already do.
   */
  onConfirm?: () => unknown
  /** Confirm button label while `onConfirm` is pending. Defaults to `confirmLabel`. */
  pendingLabel?: string
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>

const ConfirmContext = createContext<ConfirmFn | null>(null)

function TypeToConfirmField({
  gate,
  value,
  onChange,
}: {
  gate: ConfirmTypeToConfirm
  value: string
  onChange: (next: string) => void
}) {
  const inputId = useId()
  return (
    <div className="flex flex-col gap-2">
      <label
        htmlFor={inputId}
        className="text-[var(--dlg-body)] text-dialog-meta"
      >
        {gate.label ?? (
          <>
            Type{' '}
            <span className="font-semibold text-[var(--dlg-ink)]">
              {gate.phrase}
            </span>{' '}
            to confirm
          </>
        )}
      </label>
      <Input
        id={inputId}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={gate.phrase}
        autoComplete="off"
        autoCapitalize="none"
        spellCheck={false}
      />
    </div>
  )
}

/**
 * App-wide imperative confirmation dialog.
 *
 * Usage: `const confirm = useConfirm()` then
 * `if (!(await confirm({ title: '…' }))) return`.
 *
 * Mirrors `window.confirm`'s call-site ergonomics but renders our themed,
 * accessible AlertDialog instead of a blocking native dialog.
 *
 * Critically, the dialog is mounted ONCE here at the app root (see
 * `RootProviders`), so it never unmounts when the *calling* component does.
 * Deleting a canvas email node, for example, tears the node down — but the
 * confirm dialog (and Radix's body pointer-events / scroll-lock cleanup that
 * runs on its close) lives safely above it. That is the core fix for the
 * "page locked / unclickable after confirming a delete" bug.
 */
/** The pre-v2 confirm chrome, for `classic` confirms (the Send dialog). */
function renderClassicConfirm({
  title,
  description,
  body,
  actions,
}: ConfirmDialogRenderSlots) {
  return (
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>{title}</AlertDialogTitle>
        {description != null ? (
          <AlertDialogDescription>{description}</AlertDialogDescription>
        ) : null}
      </AlertDialogHeader>
      {body ?? null}
      <AlertDialogFooter>{actions}</AlertDialogFooter>
    </AlertDialogContent>
  )
}

export function ConfirmDialogProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false)
  const [options, setOptions] = useState<ConfirmOptions | null>(null)
  const [typedText, setTypedText] = useState('')
  const [isPending, setIsPending] = useState(false)
  // Holds the pending promise's `resolve`. Whoever settles the dialog first
  // nulls the ref, so a close fired *after* a confirm can't double-resolve.
  const resolverRef = useRef<((value: boolean) => void) | null>(null)
  // The request whose `onConfirm` is running. A newer `confirm()` call
  // supersedes the dialog; the older action must then not close it.
  const requestIdRef = useRef(0)
  const optionsRef = useRef<ConfirmOptions | null>(null)
  const isPendingRef = useRef(false)

  const settle = useCallback((value: boolean) => {
    const resolve = resolverRef.current
    resolverRef.current = null
    resolve?.(value)
  }, [])

  const confirm = useCallback<ConfirmFn>(
    (nextOptions) => {
      // A fresh request supersedes any still-pending one — resolve the old
      // promise false so its awaiter never hangs.
      settle(false)
      requestIdRef.current += 1
      optionsRef.current = nextOptions
      isPendingRef.current = false
      setIsPending(false)
      setOptions(nextOptions)
      setTypedText('')
      setIsOpen(true)
      return new Promise<boolean>((resolve) => {
        resolverRef.current = resolve
      })
    },
    [settle]
  )

  const handleConfirm = useCallback(() => {
    const current = optionsRef.current
    if (!current?.onConfirm) {
      settle(true)
      setIsOpen(false)
      return
    }
    if (isPendingRef.current) {
      return
    }
    const requestId = requestIdRef.current
    isPendingRef.current = true
    setIsPending(true)
    const finish = () => {
      // Only the request that started the action may close the dialog; a
      // newer request has its own lifecycle.
      if (requestIdRef.current !== requestId) {
        return
      }
      isPendingRef.current = false
      setIsPending(false)
      settle(true)
      setIsOpen(false)
    }
    void Promise.resolve()
      .then(() => current.onConfirm?.())
      .then(finish, (error: unknown) => {
        finish()
        throw error
      })
  }, [settle])

  const handleOpenChange = useCallback(
    (next: boolean) => {
      // While `onConfirm` runs the action owns the close — Esc and overlay
      // clicks must not dismiss a dialog whose work is still in flight.
      if (!next && isPendingRef.current) {
        return
      }
      // Esc, overlay click, Cancel, or any programmatic close rejects.
      // No-op when `handleConfirm` already cleared the resolver.
      if (!next) {
        settle(false)
      }
      setIsOpen(next)
    },
    [settle]
  )

  // Never strand an awaiter if the provider itself unmounts.
  useEffect(() => () => settle(false), [settle])

  const gate = options?.typeToConfirm
  const isConfirmEnabled = !gate || typedText === gate.phrase
  const confirmLabel = options?.confirmLabel ?? 'Confirm'
  const confirmButtonLabel = isPending
    ? (options?.pendingLabel ?? confirmLabel)
    : confirmLabel

  const actions = (
    <>
      <AlertDialogCancel disabled={isPending}>
        {options?.cancelLabel ?? 'Cancel'}
      </AlertDialogCancel>
      <Button
        variant={options?.destructive ? 'destructive' : 'default'}
        onClick={handleConfirm}
        disabled={!isConfirmEnabled || isPending}
        aria-busy={isPending || undefined}
      >
        {confirmButtonLabel}
      </Button>
    </>
  )

  const body =
    options?.body != null || gate ? (
      <>
        {options?.body}
        {gate ? (
          <TypeToConfirmField
            gate={gate}
            value={typedText}
            onChange={setTypedText}
          />
        ) : null}
      </>
    ) : undefined

  const slots = {
    title: options?.title,
    description: options?.description,
    body,
    actions,
  }
  const customContent =
    options?.contentRenderer?.(slots) ??
    (options?.classic ? renderClassicConfirm(slots) : null)

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <AlertDialog open={isOpen} onOpenChange={handleOpenChange}>
        {customContent ?? (
          <BrewAlertDialogContent size="sm">
            <BrewDialogHeader
              kind="alert"
              tone={options?.destructive ? 'danger' : 'default'}
              medal={
                <BrewDialogMedal
                  icon={
                    options?.icon ??
                    (options?.destructive ? AlertTriangleIcon : CircleHelpIcon)
                  }
                  tone={options?.destructive ? 'danger' : 'default'}
                />
              }
              title={options?.title}
              description={options?.description ?? undefined}
            />
            {body ? <BrewDialogBody>{body}</BrewDialogBody> : null}
            <BrewDialogFooter>{actions}</BrewDialogFooter>
          </BrewAlertDialogContent>
        )}
      </AlertDialog>
    </ConfirmContext.Provider>
  )
}

/** Returns the imperative `confirm(options) => Promise<boolean>` function. */
export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext)
  if (!ctx) {
    throw new Error('useConfirm must be used within a <ConfirmDialogProvider>')
  }
  return ctx
}
